import React, { useState } from 'react';
import {
  ShoppingCart,
  Building,
  DollarSign,
  Calendar,
  Search,
  Filter,
  Eye,
  Edit,
  Plus,
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Users,
  Package,
  Zap
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface Supplier {
  id: string;
  name: string;
  category: 'pharmaceutical' | 'medical_devices' | 'consumables' | 'equipment';
  contactPerson: string;
  email: string;
  phone: string;
  rating: number;
  totalSpend: number;
  lastOrder: string;
  leadTime: number;
  status: 'active' | 'inactive' | 'suspended';
  contracts: Contract[];
}

interface Contract {
  id: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  value: number;
  status: 'active' | 'expired' | 'pending_renewal';
  terms: string;
  rebatePercentage: number;
}

interface ProcurementOpportunity {
  id: string;
  title: string;
  category: string;
  estimatedValue: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'research' | 'rfq' | 'negotiation' | 'awarded' | 'cancelled';
  dueDate: string;
  assignedTo: string;
  suppliers: string[];
  savings: number;
}

const Procurement: React.FC = () => {
  const { user } = useAuth();
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateOpportunity, setShowCreateOpportunity] = useState(false);

  const mockSuppliers: Supplier[] = [
    {
      id: 'sup-001',
      name: 'Sigma Healthcare',
      category: 'pharmaceutical',
      contactPerson: 'Sarah Wilson',
      email: 'procurement@sigma.com.au',
      phone: '(02) 9876 5432',
      rating: 4.5,
      totalSpend: 125000.50,
      lastOrder: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
      leadTime: 3,
      status: 'active',
      contracts: [
        {
          id: 'con-001',
          contractNumber: 'CON-2024-001',
          startDate: '2024-01-01',
          endDate: '2024-12-31',
          value: 500000,
          status: 'active',
          terms: 'Net 30 days, 2% rebate on volume',
          rebatePercentage: 2.0
        }
      ]
    },
    {
      id: 'sup-002',
      name: 'API (Australian Pharmaceutical Industries)',
      category: 'pharmaceutical',
      contactPerson: 'Michael Chen',
      email: 'orders@api.net.au',
      phone: '(02) 8765 4321',
      rating: 4.2,
      totalSpend: 87500.25,
      lastOrder: format(subDays(new Date(), 10), 'yyyy-MM-dd'),
      leadTime: 2,
      status: 'active',
      contracts: []
    },
    {
      id: 'sup-003',
      name: 'Medtronic Australia',
      category: 'medical_devices',
      contactPerson: 'Dr. Lisa Johnson',
      email: 'sales@medtronic.com.au',
      phone: '(02) 7654 3210',
      rating: 4.8,
      totalSpend: 45000.00,
      lastOrder: format(subDays(new Date(), 15), 'yyyy-MM-dd'),
      leadTime: 7,
      status: 'active',
      contracts: []
    }
  ];

  const mockOpportunities: ProcurementOpportunity[] = [
    {
      id: 'opp-001',
      title: 'Annual Pharmaceutical Supply Contract',
      category: 'pharmaceutical',
      estimatedValue: 750000,
      priority: 'high',
      status: 'negotiation',
      dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      assignedTo: 'Sarah Johnson',
      suppliers: ['Sigma Healthcare', 'API (Australian Pharmaceutical Industries)'],
      savings: 15000
    },
    {
      id: 'opp-002',
      title: 'Medical Device Equipment Upgrade',
      category: 'medical_devices',
      estimatedValue: 125000,
      priority: 'medium',
      status: 'rfq',
      dueDate: format(addDays(new Date(), 45), 'yyyy-MM-dd'),
      assignedTo: 'Michael Chen',
      suppliers: ['Medtronic Australia', 'Johnson & Johnson'],
      savings: 5000
    },
    {
      id: 'opp-003',
      title: 'Consumables Bulk Purchase',
      category: 'consumables',
      estimatedValue: 25000,
      priority: 'low',
      status: 'research',
      dueDate: format(addDays(new Date(), 60), 'yyyy-MM-dd'),
      assignedTo: 'John Smith',
      suppliers: ['Baxter Healthcare', 'Fresenius Kabi'],
      savings: 2500
    }
  ];

  const filteredSuppliers = mockSuppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.contactPerson.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || supplier.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || supplier.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'pharmaceutical': return 'bg-blue-100 text-blue-800';
      case 'medical_devices': return 'bg-green-100 text-green-800';
      case 'consumables': return 'bg-orange-100 text-orange-800';
      case 'equipment': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOpportunityStatusColor = (status: string) => {
    switch (status) {
      case 'research': return 'bg-blue-100 text-blue-800';
      case 'rfq': return 'bg-yellow-100 text-yellow-800';
      case 'negotiation': return 'bg-orange-100 text-orange-800';
      case 'awarded': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateOpportunity = () => {
    setShowCreateOpportunity(true);
  };

  const handleViewSupplier = (supplierId: string) => {
    console.log(`Viewing supplier ${supplierId}`);
  };

  const handleEditContract = (contractId: string) => {
    console.log(`Editing contract ${contractId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Strategic Procurement 🛒
          </h1>
          <p className="text-gray-600 mt-2">Manage supplier relationships, contracts, and strategic sourcing opportunities for {user?.branch}.</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleCreateOpportunity}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Opportunity</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Generate Report</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
            <Building className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Active Suppliers</p>
            <p className="text-2xl font-bold text-gray-900">{mockSuppliers.filter(s => s.status === 'active').length}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-green-100 text-green-600">
            <DollarSign className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Spend</p>
            <p className="text-2xl font-bold text-gray-900">${mockSuppliers.reduce((sum, s) => sum + s.totalSpend, 0).toLocaleString()}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-orange-100 text-orange-600">
            <Target className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Active Opportunities</p>
            <p className="text-2xl font-bold text-gray-900">{mockOpportunities.filter(o => o.status !== 'awarded' && o.status !== 'cancelled').length}</p>
          </div>
        </div>
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Potential Savings</p>
            <p className="text-2xl font-bold text-gray-900">${mockOpportunities.reduce((sum, o) => sum + o.savings, 0).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search suppliers, contacts..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="pharmaceutical">Pharmaceutical</option>
            <option value="medical_devices">Medical Devices</option>
            <option value="consumables">Consumables</option>
            <option value="equipment">Equipment</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>

          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Suppliers and Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Suppliers List */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Supplier Directory</h3>
          <div className="space-y-4">
            {filteredSuppliers.map((supplier) => (
              <div 
                key={supplier.id} 
                className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                  selectedSupplier === supplier.id ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedSupplier(supplier.id)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-600" />
                      <span className="font-semibold text-gray-900">{supplier.name}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(supplier.category)}`}>
                      {supplier.category.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(supplier.status)}`}>
                      {supplier.status.toUpperCase()}
                    </span>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-gray-900">{supplier.rating}</span>
                      <span className="text-yellow-500">★</span>
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="font-medium text-gray-900">{supplier.contactPerson}</p>
                  <p className="text-sm text-gray-600">{supplier.email}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>${supplier.totalSpend.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{supplier.leadTime} days</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Last order: {format(new Date(supplier.lastOrder), 'dd/MM/yyyy')}
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewSupplier(supplier.id);
                    }}
                    className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Procurement Opportunities */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Procurement Opportunities</h3>
          <div className="space-y-4">
            {mockOpportunities.map((opportunity) => (
              <div key={opportunity.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Target className="h-4 w-4 text-gray-600" />
                    <span className="font-semibold text-gray-900">{opportunity.title}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(opportunity.priority)}`}>
                      {opportunity.priority.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getOpportunityStatusColor(opportunity.status)}`}>
                      {opportunity.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="text-sm text-gray-600">Category: {opportunity.category}</p>
                  <p className="text-sm text-gray-600">Assigned to: {opportunity.assignedTo}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>Value: ${opportunity.estimatedValue.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <TrendingUp className="h-4 w-4" />
                      <span>Savings: ${opportunity.savings.toLocaleString()}</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Due: {format(new Date(opportunity.dueDate), 'dd/MM/yyyy')}
                  </div>
                  <div className="flex space-x-2">
                    <button className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-200">
                      <Eye className="h-3 w-3" />
                    </button>
                    <button className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700">
                      <Edit className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Procurement;
